import { useEffect, useRef, useState } from 'react';
import { rands, totalReturn, type FavTag } from '../utils/betting';

interface OddsButtonProps {
  selectionId: string;
  /** Friendly, already-transformed label (e.g. "Mexico", "More than 2.5 goals"). */
  selectionName: string;
  odds: number;
  isSelected: boolean;
  isDisabled: boolean;
  onClick: () => void;
  /** favourite / underdog cue for first-timers. */
  favTag?: FavTag;
  /** Stake the "you'd get" hint is based on (rands). */
  stakeRands?: number;
}

type Flash = 'up' | 'down' | null;

export default function OddsButton({
  selectionName,
  odds,
  isSelected,
  isDisabled,
  onClick,
  favTag = null,
  stakeRands = 100,
}: OddsButtonProps) {
  const prevOdds = useRef<number>(odds);
  const [flash, setFlash] = useState<Flash>(null);
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (prevOdds.current !== odds) {
      const direction: Flash = odds > prevOdds.current ? 'up' : 'down';
      prevOdds.current = odds;
      if (flashTimer.current) clearTimeout(flashTimer.current);
      setFlash(direction);
      flashTimer.current = setTimeout(() => setFlash(null), 2000);
    }
    return () => {
      if (flashTimer.current) clearTimeout(flashTimer.current);
    };
  }, [odds]);

  const base =
    'relative flex min-h-[64px] w-full flex-col items-start justify-center gap-0.5 rounded-button border px-3 py-2 text-left transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-primary/40';

  const state = isDisabled
    ? 'cursor-not-allowed border-border bg-surface-2 opacity-50'
    : isSelected
    ? 'cursor-pointer border-primary bg-primary text-white shadow-btn'
    : flash === 'up'
    ? 'cursor-pointer border-success bg-success/10'
    : flash === 'down'
    ? 'cursor-pointer border-error bg-error/10'
    : 'cursor-pointer border-border bg-surface hover:border-primary hover:bg-primary/5';

  const muted = isSelected ? 'text-white/80' : 'text-text-muted';
  const strong = isSelected ? 'text-white' : 'text-text-primary';

  return (
    <button
      onClick={isDisabled ? undefined : onClick}
      disabled={isDisabled}
      className={`${base} ${state}`}
      aria-pressed={isSelected}
    >
      {/* favourite / underdog chip */}
      {favTag && !isSelected && (
        <span
          className={`absolute right-2 top-2 rounded-pill px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
            favTag === 'favourite'
              ? 'bg-primary/10 text-primary'
              : 'bg-surface-2 text-text-muted'
          }`}
        >
          {favTag === 'favourite' ? '★ Favourite' : 'Underdog'}
        </span>
      )}

      <span className={`text-sm font-semibold leading-tight ${strong}`}>
        {selectionName}
      </span>

      <div className="flex flex-wrap items-baseline gap-x-1.5">
        <span className={`font-mono text-base font-bold tabular-nums ${strong}`}>
          {odds.toFixed(2)}
        </span>
        <span className={`text-[11px] ${muted}`}>
          {rands(stakeRands)} →{' '}
          <span className="font-semibold">{rands(totalReturn(odds, stakeRands))}</span>
        </span>
      </div>
    </button>
  );
}
